/**
 * Module: HomeScreen
 * Role: Main landing screen — IOSScreen wrapper with HomeHeader (balance lives in the
 *       header pill), InProgressFlowsBlock, RecommendationsList. Composed from atoms only.
 *       FTR-1106 Task 4: promo block «Стать креатором» for standard buyers with no active slots.
 *
 * Uses:
 *   - components/atoms:IOSScreen
 *   - components/atoms:Card
 *   - screens/HomeScreen/HomeHeader (sticky glass header — logo + копилка pill + bell)
 *   - screens/HomeScreen/InProgressFlowsBlock (fetches active flow cards)
 *   - screens/HomeScreen/RecommendationsList (fetches /api/v2/buyer/tasks, 2-col grid)
 *   - @tanstack/react-query:useQuery
 *   - api/client:apiFetch
 *   - react-router-dom:useNavigate
 *   - styles/tokens:P, S, T
 *
 * Used by:
 *   - App:default (Route "/")
 *
 * Glossary: ai/glossary/buyer.md
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import { IOSScreen, Card } from '../../components/atoms';
import { HomeHeader } from './HomeHeader';
import { InProgressFlowsBlock } from './InProgressFlowsBlock';
import { RecommendationsList } from './RecommendationsList';
import { CoachmarksOverlay } from '../onboarding/CoachmarksOverlay';
import { COACHMARKS_SEEN_KEY, type CoachmarksState } from '../onboarding/coachmarks';
import { P, S, T } from '../../styles/tokens';
import { Ic } from '../../components/atoms';

// ─── Inline types ─────────────────────────────────────────────────────────────
// Minimal slice of ProfileView needed for the creator promo gate.
// Full ProfileView lives in ProfileScreen.tsx (L-240: no shared api/types.ts).
interface ProfileCreatorSlice {
  account_kind?: 'standard' | 'creator';
  creator_status?: string | null;
}

interface SlotListResponse {
  items: Array<{ id: string; status: string }>;
  meta: { total: number };
}

// ─── CreatorPromoCard ─────────────────────────────────────────────────────────
// Shown only to standard buyers with no active in-progress slot.
// ADR-022 §7: navigate() — НИКАКОГО openExternal(BOT_LINK).
interface CreatorPromoCardProps {
  onTap: () => void;
}

function CreatorPromoCard({ onTap }: CreatorPromoCardProps) {
  return (
    <Card
      hover
      onClick={onTap}
      style={{ cursor: 'pointer', background: P.cream, border: `1px solid ${P.line}` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: P.terracotta,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Ic name="flame" size={22} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ font: T.headline, color: S.label, margin: '0 0 2px' }}>
            Стань креатором
          </p>
          <p style={{ font: T.footnote, color: S.secondaryLabel, margin: 0 }}>
            Получай премиум-задания с высоким кешбэком
          </p>
        </div>
        <Ic name="chevR" size={16} color={P.terracotta} style={{ flexShrink: 0 }} />
      </div>
    </Card>
  );
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  // FTR-1114: one-time coachmarks tour. PreparingStep navigates here with
  // state.showCoachmarks=true; we fire the tour exactly once per session
  // (sessionStorage guard) so a refresh / deep-link / normal home visit never re-triggers.
  const [showCoachmarks, setShowCoachmarks] = useState(false);
  useEffect(() => {
    const navState = location.state as CoachmarksState | null;
    if (!navState?.showCoachmarks) return;
    if (sessionStorage.getItem(COACHMARKS_SEEN_KEY)) return;
    sessionStorage.setItem(COACHMARKS_SEEN_KEY, '1');
    setShowCoachmarks(true);
  }, [location.state]);

  // FTR-1106 Task 4: check profile + active slots to decide whether to show promo.
  // queryKey ['buyer','profile'] matches ProfileScreen — shared cache (no extra request).
  const { data: profile } = useQuery<ProfileCreatorSlice>({
    queryKey: ['buyer', 'profile'],
    queryFn: () => apiFetch<ProfileCreatorSlice>('/api/v2/buyer/profile'),
    retry: false,
  });

  // queryKey ['slots','active'] matches ActiveTaskPill — shared cache (no extra request).
  const { data: slotsData } = useQuery<SlotListResponse>({
    queryKey: ['slots', 'active'],
    queryFn: () => apiFetch<SlotListResponse>('/api/v2/buyer/slots?status=active&limit=1'),
    retry: false,
  });

  // Show promo only if: standard buyer (no creator role yet) + no active slot.
  const isStandard = !profile?.account_kind || profile.account_kind === 'standard';
  const hasActiveSlot = (slotsData?.items.length ?? 0) > 0;
  const showCreatorPromo = isStandard && !hasActiveSlot;

  return (
    <>
    <IOSScreen tabBarSpace>
      <HomeHeader />
      {/* Visually-hidden h1 for screen-reader accessibility (S3 — BUG-1046) */}
      <h1
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Awardy
      </h1>
      {/* Block A: in-progress flows + creator promo — standard iOS gutter.
          Balance now lives in HomeHeader's «копилка» pill (no WalletCard hero). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px 16px 12px' }}>
        <InProgressFlowsBlock />
        {/* FTR-1106 Task 4: creator promo — only for standard buyers without active slot */}
        {showCreatorPromo && (
          <CreatorPromoCard onTap={() => navigate('/creator-onboarding/promo')} />
        )}
      </div>
      {/* Block B: RecommendationsList — 12px side padding so 2-col grid breathes */}
      <div style={{ padding: '0 12px 16px' }}>
        <RecommendationsList />
      </div>
    </IOSScreen>
    {showCoachmarks && <CoachmarksOverlay onDone={() => setShowCoachmarks(false)} />}
    </>
  );
}
